import moment from "moment";
import React from "react";
import {Button, CheckBox, ColorPickerBox, Column, DropDown, DropDownContent, DropDownTrigger, Pre, Row, Select, Spinner, Text, TextInput, TimeSpanInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {Chroma, Chroma_Safe, InfoButton, Observer, RunInAction_Set, TextPlus, VDateTime} from "web-vcore";
import {ScrollView} from "react-vscrollview";
import {Clone, E, GetEntries} from "js-vextensions";
import {GraphRenderType_values, SmoothingType} from "../../Store/main/tools/journey.js";
import {store} from "../../Store/index.js";
import {DateMarker, MarkerFromMarkerOrDate, RangeType, ShowTechExecution, StatView} from "../../Server/DB/statViews/@StatView.js";
import {zIndexes} from "../../Utils/UI/ZIndexes.js";
import {observer_mgl} from "mobx-graphlink";
import {StatsPage_GetTopOffsetForDropdown} from "./ViewsDropdown.js";
import {GetJournalEntries} from "../../Server/DB/journalEntries.js";
import {MeID} from "../../Server/DB/users.js";
import {ShowSpecialStatsDialog} from "./SpecialStatsDialog.js";
import {SpinnerForPercent} from "../../Utils/ReactComponents/SpinnerForPercent.js";

export const StatViewSettingsDropdown = ()=>{
	return (
		<DropDown style={{marginLeft: "auto", marginRight: 5, position: "relative"}}>
			<DropDownTrigger>
				<Button mdIcon="cog" size={32}/>
			</DropDownTrigger>
			<DropDownContent style={{
				zIndex: zIndexes.dropDown, position: "fixed", right: 0,
				top: StatsPage_GetTopOffsetForDropdown(),
				bottom: 0, // bottom:0 = stop at bottom of screen
				background: "rgba(0,0,0,.9)", borderRadius: 5,
			}}>
				<ScrollView style={{height: "100%"}}>
					<StatViewDetailsUI/>
				</ScrollView>
			</DropDownContent>
		</DropDown>
	);
};

const StatViewDetailsUI = observer_mgl(()=>{
	const {view} = store.main.stats;
	//const dreams = GetJournalEntries(MeID());

	const Change = (mutator: (view: StatView)=>any)=>{
		RunInAction_Set(()=>{
			//mutator(view);
			const newData = Clone(view);
			mutator(newData);
			store.main.stats.view = newData;
		});
	};

	return (
		<>
			<Row>
				<Text>Render type:</Text>
				<Select ml={5} options={GraphRenderType_values} value={view.renderType} onChange={val=>Change(a=>a.renderType = val)}/>
			</Row>
			{view.renderType != "bars" &&
			<Row mt={5} center>
				<TextPlus info={`
					Note: The averaging is performed over any x-values *that have data-points*.
					* Example: If smoothing=30, but only 10 days have data-points, the other 20 contribute neither to the numerator nor denominator.
					* Exception: If "solid" is enabled, then (in-range) x-values without data-points are treated as having a y-value of zero.
				`.AsMultiline(0)}>Smoothing:</TextPlus>
				<Spinner ml={5} min={0} max={100} value={view.smoothing} onChange={val=>Change(a=>a.smoothing = val)}/>
				<CheckBox ml={5} text="Prev" value={view.smoothingType == SmoothingType.previous} onChange={val=>Change(a=>a.smoothingType = val ? SmoothingType.previous : SmoothingType.centered)}/>
				<CheckBox ml={5} text="Solid" value={view.replaceYValNullWith != null} onChange={val=>Change(a=>a.replaceYValNullWith = val ? 0 : null)}/>
			</Row>}
			<Row mt={5} center>
				<TextPlus info={`
					What percentage of samples to keep: 0% = keep only median value, 100% = keep all (average)
					Note: This only applies to a *non-percentage* y-types with the "(typical)" marker. (ie. those using averaging)
				`.AsMultiline(0)}>Keep %:</TextPlus>
				<Spinner ml={5} min={0} max={100} step={5} enforceRange={true} instant style={{width: 50}}
					value={view.middleKeepPercent?.ToPercent()} onChange={val=>Change(a=>a.middleKeepPercent = val.FromPercent())}/>
				<Text>%</Text>
			</Row>
			{view.xType == "dayOffset" && <>
				<Row mt={5} center>
					<Text>Day range:</Text>
					<Select ml={5} displayType="button bar" options={GetEntries(RangeType, "ui")} value={view.dayRange_type} onChange={val=>Change(a=>a.dayRange_type = val)}/>
				</Row>
				{view.dayRange_type == RangeType.start &&
				<Row mt={5} ml={5} center>
					<Pre>Start:</Pre>
					<VDateTime dateFormat="YYYY-MM-DD" timeFormat={false} inputProps={{style: {marginLeft: 5, width: 150}}}
						value={view.dayRange_start ? moment(view.dayRange_start) : null}
						onChange={val=>val && Change(a=>a.dayRange_start = val.valueOf())}/>
				</Row>}
				{view.dayRange_type == RangeType.count &&
				<Row mt={5} ml={5} center>
					<Pre>Count:</Pre>
					<Spinner ml={5} min={1} value={view.dayRange_count} onChange={val=>Change(a=>a.dayRange_count = val)}/>
					<Text ml={5}>(start: {view.dayRange_count ? moment().add(-(view.dayRange_count - 1), "days").format("MMM D") : "n/a"})</Text>
				</Row>}
				<Row mt={5} ml={5} center>
					<Pre>Omit last X:</Pre>
					<Spinner ml={5} min={0} value={view.days_omitLastX} onChange={val=>Change(a=>a.days_omitLastX = val)}/>
					<StatViewSettingLockUI field="days_omitLastX"/>
				</Row>
			</>}
			{view.xType == "dayInGroup" && <>
				<Row mt={5} center>
					<Pre>Days:</Pre>
					<Spinner ml={5} min={1} value={view.dayRange_count} onChange={val=>Change(a=>a.dayRange_count = val)}/>
				</Row>
			</>}
			{view.xType == "cycleInGroup" &&
			<Row mt={5} center>
				<Pre>Cycles:</Pre>
				<Spinner ml={5} min={1} value={view.cyclesToShow} onChange={val=>Change(a=>a.cyclesToShow = val)}/>
			</Row>}
			<Row>Dream matcher:</Row>
			<Column mt={5} ml={10}>
				<Row>
					<TextPlus info={`
						Pad with "/" to use a regex, like this: /text matches are checked on dream (short|long) text/
					`.AsMultiline(0)}>Text:</TextPlus>
					<TextInput ml={5} style={{flex: 1}} value={view.dreamMatcher_text} onChange={val=>Change(a=>a.dreamMatcher_text = val)}/>
				</Row>
			</Column>
			<CheckBox mt={5} text="Show lucid count" value={view.showLucidCount ?? false} onChange={val=>Change(a=>a.showLucidCount = val)}/>
			<CheckBox mt={5} text="Show lucid rate" value={view.showLucidRate ?? false} onChange={val=>Change(a=>a.showLucidRate = val)}/>
			<CheckBox mt={5} text="Show night count" value={view.showNightCount ?? false} onChange={val=>Change(a=>a.showNightCount = val)}/>
			<Row mt={5} center>
				<TextPlus info={`
					Displays the tech-execution quality (pre-sleep) above each bar.
					* Percent: shows the highest tech-execution quality value among segments for that bar, as a percentage.
					* Binary: shows "T✔" if at least one segment had a quality value at or above the "min for pass" threshold (default: 100%), else nothing.
				`.AsMultiline(0)}>Tech quality (pre-sleep):</TextPlus>
				<Select ml={5} options={GetEntries(ShowTechExecution, "ui")}
					value={view.showTechExecution ?? ShowTechExecution.hidden} onChange={val=>Change(a=>a.showTechExecution = val)}/>
			</Row>
			{view.showTechExecution == ShowTechExecution.binary &&
			<Row mt={5} ml={10} center>
				<TextPlus info="Minimum quality (0%-100%) for a segment to count as a pass in Binary mode. If 0%/unset, defaults to 100%.">Min for pass:</TextPlus>
				<SpinnerForPercent ml={5} min={0} max={100} step={10} enforceRange={true} instant style={{width: 60}}
					value={view.techExecution_minForPass} onChange={val=>Change(a=>a.techExecution_minForPass = val)}/>
			</Row>}
			<Row mt={5}>
				<TextPlus info="(0 = no limit)">Max MP recall gap:</TextPlus>
				<TimeSpanInput ml={5} largeUnit="hour" smallUnit="minute" value={view.maxMemoryPromptRecallGap} onChange={val=>Change(a=>a.maxMemoryPromptRecallGap = val)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Normalize group metrics:" value={view.normalizeGroupMetrics ?? false} onChange={val=>Change(a=>a.normalizeGroupMetrics = val)}/>
				<InfoButton ml={5} text={`
					Problem: When comparing alarm-delay groups, metrics for shorter-delays look worse due to hits being spread among more cycles.
					Solution: This normalizes certain metrics, making their values comparable for evaluating the "total of X" to expect per night.
					Application: These metrics are affected (if grouping is "Alarm delay"): Tags (typical), Words (typical), and lucidity-rate annotation
					Example: If standard alarm-delay is 80mins, a per-cycle lucidity-rate of 5% in a 40mins alarm-delay group gets normalized to 10% for display.
				`.AsMultiline(0)}/>
			</Row>
			{view.normalizeGroupMetrics &&
			<Row mt={5} ml={10}>
				<Text>Standard alarm delay:</Text>
				<Spinner ml={5} value={view.normalizeGroupMetrics_alarmDelay} onChange={val=>Change(a=>a.normalizeGroupMetrics_alarmDelay = val)}/>
			</Row>}
			<Row mt={5}>
				<CheckBox text="Lucidity:" value={view.lucidityLevels != null} onChange={val=>{
					Change(a=>a.lucidityLevels = val ? ["nonLucid", "semiLucid", "lucid"] : null);
				}}/>
				<InfoButton ml={5} text="Note that this filter only applies to journal-segment-derived metrics (eg. word-count), not [session/event]-derived metrics."/>
				<CheckBox ml={5} text="NL" title="Non-lucid" enabled={view.lucidityLevels != null} value={view.lucidityLevels?.includes("nonLucid") ?? false} onChange={val=>{
					Change(a=>{
						if (val) a.lucidityLevels!.push("nonLucid");
						else a.lucidityLevels!.Remove("nonLucid");
					});
				}}/>
				<CheckBox ml={5} text="SL" title="Semi-lucid" enabled={view.lucidityLevels != null} value={view.lucidityLevels?.includes("semiLucid") ?? false} onChange={val=>{
					Change(a=>{
						if (val) a.lucidityLevels!.push("semiLucid");
						else a.lucidityLevels!.Remove("semiLucid");
					});
				}}/>
				<CheckBox ml={5} text="L" title="Lucid" enabled={view.lucidityLevels != null} value={view.lucidityLevels?.includes("lucid") ?? false} onChange={val=>{
					Change(a=>{
						if (val) a.lucidityLevels!.push("lucid");
						else a.lucidityLevels!.Remove("lucid");
					});
				}}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Date range:" value={view.dateRange_enabled ?? false} onChange={val=>Change(a=>a.dateRange_enabled = val)}/>
				<InfoButton ml={5} text={`
					Note that this also filters the pool used for applying the engine Journey component's "Option catch-up rate" system.
				`.AsMultiline(0)}/>
			</Row>
			{view.dateRange_enabled && <>
				<Row mt={5}>
					<Text mr={5} style={{flex: 1}}>From:</Text>
					<VDateTime dateFormat="YYYY-MM-DD" timeFormat="HH:mm:ss" inputProps={{style: {width: 150}}}
						value={view.dateRange_min ? moment(view.dateRange_min) : null}
						onChange={val=>Change(a=>a.dateRange_min = val?.valueOf() ?? null)}/>
				</Row>
				<Row mt={5}>
					<Text mr={5} style={{flex: 1}}>To:</Text>
					<VDateTime dateFormat="YYYY-MM-DD" timeFormat="HH:mm:ss" inputProps={{style: {width: 150}}}
						value={view.dateRange_max ? moment(view.dateRange_max) : null}
						onChange={val=>Change(a=>a.dateRange_max = val?.valueOf() ?? null)}/>
				</Row>
			</>}
			<Row>
				<CheckBox text="Date markers:" value={view.showDateMarkers ?? false} onChange={val=>Change(a=>a.showDateMarkers = val)}/>
				<Button ml={5} mdIcon="plus" onClick={()=>Change(a=>a.dateMarkers = (a.dateMarkers ?? []).concat(new Date().setHours(0, 0, 0, 0)))}/>
			</Row>
			{view.showDateMarkers && view.dateMarkers != null && view.dateMarkers.map((markerOrDate, index)=>{
				const marker = MarkerFromMarkerOrDate(markerOrDate);
				const ChangeMarker = (changerFunc: (marker: DateMarker)=>any)=>{
					const newMarker = Clone(marker);
					changerFunc(newMarker);
					Change(a=>a.dateMarkers![index] = newMarker);
				};
				return (
					<Row key={index} mt={5}>
						<VDateTime dateFormat="YYYY-MM-DD" timeFormat="HH:mm:ss" inputProps={{style: {width: 135, fontSize: 13}}}
							value={marker?.date ? moment(marker?.date) : null}
							onChange={val=>ChangeMarker(a=>a.date = val?.valueOf() ?? null)}/>
						<Button ml={5} mdIcon="delete" size={23} onClick={()=>Change(a=>a.dateMarkers!.RemoveAt(index))}/>
						<CheckBox ml={5} text="C" value={marker?.color != null} onChange={val=>ChangeMarker(a=>a.color = val ? "rgba(255,255,0,.7)" : null)}/>
						{view.dateMarkers![index]?.["color"] != null &&
							<ColorPickerBox ml={5} color={Chroma_Safe(marker?.color ?? "").rgba()} onChange={color=>ChangeMarker(a=>a.color = Chroma(color).css())}/>}
					</Row>
				);
			})}

			<Row>
				<TextPlus info={`
					You can rename/merge groups using this section; field 1 is the from-group (ie. group to affect), field 2 is the to-group (ie. new name).
					Note: You can also hide/exclude groups from the chart, by setting the to-group to an empty string. 
				`.AsMultiline(0)}>Group renames:</TextPlus>
				<Button ml={5} mdIcon="plus" onClick={()=>Change(a=>a.groupRenames = (a.groupRenames ?? []).concat({from: "", to: ""}))}/>
			</Row>
			{view.groupRenames != null && view.groupRenames.map((merge, index)=>{
				return (
					<Row key={index} mt={5}>
						<TextInput style={{flex: 1, width: 70}} value={merge.from} onChange={val=>Change(a=>a.groupRenames![index] = {...merge, from: val})}/>
						<TextInput style={{flex: 1, width: 70}} value={merge.to} onChange={val=>Change(a=>a.groupRenames![index] = {...merge, to: val})}/>
						<Button mdIcon="delete" onClick={()=>Change(a=>a.groupRenames!.RemoveAt(index))}/>
					</Row>
				);
			})}

			<Row mt={5}>Label overrides:</Row>
			<Row mt={5}>
				<Text>Y-axis:</Text>
				<TextInput ml={5} style={{flex: 1}} value={view.yAxis_labelOverride} onChange={val=>Change(a=>a.yAxis_labelOverride = val)}/>
			</Row>
			<Row mt={5}>
				<Text>X-axis:</Text>
				<TextInput ml={5} style={{flex: 1}} value={view.xAxis_labelOverride} onChange={val=>Change(a=>a.xAxis_labelOverride = val)}/>
			</Row>

			<Row mt={5}>
				<CheckBox text="X-axis step override:" value={view.xAxis_stepOverride_enabled ?? false} onChange={val=>Change(a=>a.xAxis_stepOverride_enabled = val)}/>
				{view.xAxis_stepOverride_enabled &&
				<Spinner ml={5} value={view.xAxis_stepOverride} onChange={val=>Change(a=>a.xAxis_stepOverride = val)}/>}
			</Row>
			<Row mt={5}>
				<CheckBox text="X-axis max override:" value={view.xAxis_maxOverride_enabled ?? false} onChange={val=>Change(a=>a.xAxis_maxOverride_enabled = val)}/>
				{view.xAxis_maxOverride_enabled &&
				<Spinner ml={5} value={view.xAxis_maxOverride} onChange={val=>Change(a=>a.xAxis_maxOverride = val)}/>}
			</Row>
			<Row mt={5}>
				<CheckBox text="Y-axis max override:" value={view.yAxis_maxOverride_enabled ?? false} onChange={val=>Change(a=>a.yAxis_maxOverride_enabled = val)}/>
				{view.yAxis_maxOverride_enabled &&
				<Spinner ml={5} value={view.yAxis_maxOverride} onChange={val=>Change(a=>a.yAxis_maxOverride = val)}/>}
			</Row>
			<Row mt={5}>
				<Text>Legend height override:</Text>
				<Spinner ml={5} min={0} value={view.legend_heightOverride ?? 0} onChange={val=>Change(a=>a.legend_heightOverride = val == 0 ? null : val)}/>
				<Text ml={5}>(0 = auto)</Text>
			</Row>
			<Row mt={5}>
				<Button text="Show special-stats dialog" onClick={()=>{
					ShowSpecialStatsDialog();
				}}/>
			</Row>
		</>
	);
});

const StatViewSettingLockUI = observer_mgl((props: {field: keyof StatView})=>{
	const {field} = props;
	const uiState = store.main.stats;
	const isLocked = uiState.viewLocks[field] != null;
	return (
		<Button ml={5} mdIcon={isLocked ? "lock" : "lock-open-variant"} size={22}
			style={E(
				isLocked && {backgroundColor: "rgba(255,0,0,.5)"},
			)}
			title={`
				If locked, this field's value will not be changed when the view is changed.
			`.AsMultiline(0)}
			onClick={()=>{
				RunInAction_Set(()=>uiState.viewLocks[field] = isLocked ? undefined : uiState.view[field]);
			}}/>
	);
});